import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import * as $ from 'jquery';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { AddressFactory } from 'src/app/address/components/address-form/utils/address-factory';
import { UserService } from 'src/app/core/services/user/user.service';
import { User } from 'src/app/interfaces/user.interface';
import { environment } from 'src/environments/environment';
import { SalePoint } from '../../../interfaces/salePoints.interface';

declare var google: any;

@Component({
  selector: 'app-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss'],
})
export class AddressFormComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  apiLoaded: boolean = false;
  addressText: string = '';
  indicaciones: string = '';

  // Map properties Configuration
  pointGPS: { lat: number; lng: number } | null = null;
  center: google.maps.LatLngLiteral | null = null;
  zoom: number = 15;
  mapOptions: google.maps.MapOptions = {
    disableDefaultUI: true,
    clickableIcons: false,
  };

  private autocomplete?: google.maps.places.Autocomplete;
  private readonly addressFactory = new AddressFactory(6000);
  private placeComponents: google.maps.GeocoderAddressComponent[] | null = null;

  mapLoadingSpinner = false;
  private modalBound = false;

  @Input() salePoints: SalePoint[];
  @Input() user: User;

  private autocompleteInited = false;
  private placesIntervalId: any = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private cdr: ChangeDetectorRef,
    private toastr: ToastrService,
    private userService: UserService,
    private spinner: NgxSpinnerService,
  ) { }

  ngOnDestroy(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (this.placesIntervalId) {
      clearInterval(this.placesIntervalId);
      this.placesIntervalId = null;
    }

    $('#coverageFormModal').off('shown.bs.modal');
    $('#coverageFormModal').off('hidden.bs.modal');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.salePoints?.currentValue) return;

    if (!environment.production) {
      console.log('Puntos de venta actualizados:', this.salePoints);
      console.log('Usuario:', this.user);
    }
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if ((window as any).google?.maps?.places) {
      this.apiLoaded = true;
    } else {
      window.addEventListener('mapsLoaded', () => {
        this.apiLoaded = true;
        this.cdr.detectChanges();
      }, { once: true });
    }
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.waitForPlacesAndInitAutocomplete();
  }

  private fixPacContainerZIndex(): void {
    setTimeout(() => {
      const pac = document.querySelector('.pac-container') as HTMLElement | null;
      if (pac) pac.style.zIndex = '20000';
    }, 50);
  }

  private initAutocomplete(): void {
    if (!this.searchInput?.nativeElement) return;

    const Places = (window as any)?.google?.maps?.places;
    if (!Places?.Autocomplete) {
      console.error('❌ Places Autocomplete no disponible. Revisa libraries=places.');
      return;
    }

    if (this.autocomplete) return;

    this.autocomplete = new google.maps.places.Autocomplete(this.searchInput.nativeElement, {
      fields: ['formatted_address', 'geometry.location', 'address_components'],
      types: ['geocode'],
      componentRestrictions: { country: 'co' },
    });

    if (!environment.production) console.log('[Autocomplete] creado ✅');

    this.autocomplete.addListener('place_changed', () => {
      this.spinner.show('mapLoading');

      const place = this.autocomplete!.getPlace();
      const loc = place.geometry?.location;

      if (!loc) {
        this.spinner.hide('mapLoading');
        return;
      }

      const lat = loc.lat();
      const lng = loc.lng();

      this.addressText = place.formatted_address ?? '';
      this.placeComponents = place.address_components ?? null;

      this.pointGPS = { lat, lng };
      this.center = { lat, lng };

      this.cdr.detectChanges();
      this.spinner.hide('mapLoading');
    });
  }

  get canConfirm(): boolean {
    return !!this.pointGPS && !!this.addressText;
  }

  private waitForPlacesAndInitAutocomplete(): void {
    if (this.autocompleteInited) return;

    if (this.placesIntervalId) {
      clearInterval(this.placesIntervalId);
      this.placesIntervalId = null;
    }

    this.placesIntervalId = setInterval(() => {
      const hasInput = !!this.searchInput?.nativeElement;
      const hasPlaces =
        !!(window as any)?.google?.maps?.places &&
        typeof (window as any)?.google?.maps?.places?.Autocomplete === 'function';

      if (!hasInput || !hasPlaces) return;

      clearInterval(this.placesIntervalId);
      this.placesIntervalId = null;

      this.initAutocomplete();
      this.fixPacContainerZIndex();

      this.autocompleteInited = true;

      if (!environment.production) {
        console.log('[Autocomplete] inicializado ✅');
        console.log('pac-container:', document.querySelectorAll('.pac-container').length);
      }
    }, 150);
  }

  private resolveDeliveryCost(
    distanceMeters: number,
    salePoint: SalePoint | null
  ): { ok: true; deliveryCost: number } | { ok: false; reason: string } {
    const cfg: any = (salePoint as any)?.configDelivery;
    if (!cfg) return { ok: false, reason: 'Este punto de venta no tiene configuración de domicilios.' };

    const limitFree = Number(cfg.limitFreeDelivery);
    const limitCoverage = Number(cfg.limitCoberageDelivery);
    const costDelivery = Number(cfg.costDelivery);

    if (!Number.isFinite(limitFree) || !Number.isFinite(limitCoverage) || !Number.isFinite(costDelivery)) {
      return { ok: false, reason: 'Configuración de domicilios inválida.' };
    }

    if (distanceMeters > limitCoverage) return { ok: false, reason: 'No hay cobertura para tu dirección.' };

    if (distanceMeters <= limitFree) return { ok: true, deliveryCost: 6000 };

    return { ok: true, deliveryCost: costDelivery };
  }

  confirmAddress(): void {
    if (!this.pointGPS) return;

    const { address, nearestSalePoint } = this.addressFactory.buildFromGoogleAddress({
      addressText: this.addressText,
      pointGPS: this.pointGPS,
      salePoints: this.salePoints,
      components: this.placeComponents,
      extras: {
        etiqueta: '',
        indicaciones: (this.indicaciones || '').trim(),
      },
    });

    const distanceMeters = Number(address.distancia);
    const delivery = this.resolveDeliveryCost(distanceMeters, nearestSalePoint as any);

    if ('reason' in delivery) {
      this.toastr.error(delivery.reason);
      return;
    }

    (address as any).costoDomicilio = delivery.deliveryCost;
    (address as any).inventario = (nearestSalePoint as any)?.bodega || address.inventario || '';
    (address as any).salePointId = (nearestSalePoint as any)?.id ?? address.salePointId ?? null;

    this.user.direcciones = this.addressFactory.applyAsPrimary(
      (this.user.direcciones as any[]) ?? [],
      address as any
    );

    this.user.tipoEntrega = 'Domicilio';

    this.user.direccion = (address.nomenclatura && address.numeroUno && address.numeroDos)
      ? `${address.nomenclatura} ${address.numeroUno} #${address.numeroDos}`
      : this.addressText;

    if (nearestSalePoint) {
      this.user.puntoDeVentaId = (nearestSalePoint as any).id;
      this.user.puntoDeVentaNombre = (nearestSalePoint as any).nombre;
      this.user.puntoDeVentaBodega = (nearestSalePoint as any).bodega || '';
      this.user.puntoDeVentaCiudad = (nearestSalePoint as any).nombreMunicipio;
      this.user.puntoDeVentaCodMun = (nearestSalePoint as any).codigoMunicipio;
      this.user.puntoDeVentaDepto = (nearestSalePoint as any).codigoDepartamento;
      this.user.puntoDeVentaWhatsapp = (nearestSalePoint as any).whatsapp;
      this.user.puntoDeVentaTpv = (nearestSalePoint as any).tpv;
      this.user.puntoDeVentaBarrio = (nearestSalePoint as any).barrio;
    }

    this.userService.updateUserData(this.user)
      .then(() => {
        this.toastr.success('Dirección guardada exitosamente.');
        $('#closeCovarageForm')?.click();
        setTimeout(() => window.location.reload(), 200);
      })
      .catch((err) => {
        console.error('Error updateUserData:', err);
        this.toastr.error('No pudimos guardar tu dirección. Intenta nuevamente.');
      });

    $('#closeCovarageForm')?.click();
  }

  openModal(event): void {
    $(event)?.click();
    this.closeModal();
  }

  closeModal(): void {
    $('#closeCovarageForm')?.click();
  }
}
